import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import uniqBy from 'lodash.uniqby';
import isEmpty from 'lodash.isempty';
import useAxios from 'axios-hooks';

import { Button, Checkbox } from '../../../components';

const PAGE_SIZE = 50;

const ProviderFilterModal = ({ isOpen, onClose, isSaving, onSave, providerId, currentFilter }) => {
	const [providerOptions, setProviderOptions] = useState([]);
	const [providerSelections, setProviderSelections] = useState([]);
	const [firstNameSearch, setFirstNameSearch] = useState('');
	const [lastNameSearch, setLastNameSearch] = useState('');
	const [offset, setOffset] = useState(0);

	const [{ data, loading, /* error */ }, getADTProviderFilterOptions] = useAxios({
		method: 'GET',
		url: `/providers/${providerId}/get-adt-provider-filter-options`,
		params: {
			filter_options: {
				offset,
				record_limit: PAGE_SIZE
			}
		}
	}, { manual: true });

	useEffect(() => {
		if (isOpen) {
			// set up local selections
			const { provider_filter } = currentFilter;
			if (Array.isArray(provider_filter)) {
				const stringProviderIDs = provider_filter.map(({ Provider_NPI }) => ({ Provider_NPI: `${Provider_NPI}` }));
				setProviderSelections(uniqBy(stringProviderIDs, 'Provider_NPI'));
			} else {
				setProviderSelections([]);
			}

			getADTProviderFilterOptions();
		} else {
			setProviderOptions([]);
			setProviderSelections([]);
			setFirstNameSearch('');
			setLastNameSearch('');
			setOffset(0);
		}
	}, [isOpen]);

	useEffect(() => {
		if (offset > 0) {
			let name_first, name_last;
			if (!isEmpty(firstNameSearch)) {
				name_first = firstNameSearch;
			}
			if (!isEmpty(lastNameSearch)) {
				name_last = lastNameSearch;
			}
			getADTProviderFilterOptions({
				params: {
					filter_options: {
						name_first,
						name_last,
						offset,
						record_limit: PAGE_SIZE
					}
				}
			});
		}
	}, [offset]);

	useEffect(() => {
		if (!loading && data && data.filter_options && data.filter_options.provider_filter) {
			if (offset > 0) {
				setProviderOptions(uniqBy([...providerOptions, ...data.filter_options.provider_filter], 'Provider_NPI'));
			} else {
				setProviderOptions(data.filter_options.provider_filter);
			}
		}
	}, [data, loading, offset]);

	const handleSave = () => {
		onSave({
			provider_filter: providerSelections.map(provider => ({ Provider_NPI: provider.Provider_NPI })),
		});
	};

	const handleClearAll = () => {
		setProviderSelections([]);
	};

	const handleNameSearch = () => {
		let name_first, name_last;
		if (!isEmpty(firstNameSearch)) {
			name_first = firstNameSearch;
		}
		if (!isEmpty(lastNameSearch)) {
			name_last = lastNameSearch;
		}
		setOffset(0);
		getADTProviderFilterOptions({
			params: {
				filter_options: {
					name_first,
					name_last,
					record_limit: PAGE_SIZE,
					offset: 0
				}
			}
		});
	};

	const handleArrayValueToggle = ({ target }) => {
		const [prop] = target.name.split('.');
		if (target.checked) {
			setProviderSelections(uniqBy([...providerSelections, { [prop]: target.value }], 'Provider_NPI'));
		} else {
			setProviderSelections(providerSelections.filter(arrayVal => arrayVal[prop] !== target.value));
		}
	};

	const handleBumpOffset = (event) => {
		event.preventDefault();
		setOffset(offset + PAGE_SIZE);
	};

	return (
		<Modal isOpen={isOpen} contentLabel="ADT Provider Options" className="modal-dialog modal-lg filter-modal">
			<div className="modal-content">
				<div className="modal-header">
					<div className="clearfix">
						<div className="pull-right">
							<button className="btn btn-link" onClick={onClose}>
								<em className="fa fa-lg fa-times" />
							</button>
						</div>
						<h3 className="modal-title">
							ADT Provider Options
						</h3>
					</div>
				</div>
				<div className="modal-body">
					<h4>
						{(Array.isArray(providerSelections) && providerSelections.length > 0) ? `${providerSelections.length} providers selected` : 'No filter applied'}
						<Button
							title="Clear All"
							onClick={handleClearAll}
							className="ml"
							info
							small
							name="provider_filter"
						/>
					</h4>
					<div className="row">
						<div className="col-sm-12 form-inline">
							<div className="form-group">
								<label htmlFor="first-name-search" className="sr-only">First Name</label>
								<input
									id="first-name-search"
									type="text"
									placeholder="First name"
									className="form-control"
									value={firstNameSearch}
									onChange={({ target }) => setFirstNameSearch(target.value)}
								/>
							</div>
							<div className="form-group ml">
								<label htmlFor="last-name-search" className="sr-only">Last Name</label>
								<input
									id="last-name-search"
									type="text"
									placeholder="Last name"
									className="form-control"
									value={lastNameSearch}
									onChange={({ target }) => setLastNameSearch(target.value)}
								/>
							</div>
							<Button
								title="Search"
								onClick={handleNameSearch}
								primary
								className="ml"
							/>
						</div>
					</div>
					<div className="row">
						{!loading && providerOptions.length === 0 && (
							<div className="col-sm-12">No providers found</div>
						)}
						{providerOptions.map(provider => (
							<div className="col-sm-4" key={provider.Provider_NPI}>
								<Checkbox
									checked={Boolean(providerSelections.find(p => p.Provider_NPI === provider.Provider_NPI))}
									onChange={handleArrayValueToggle}
									label={`${provider.Provider_Last_Name}, ${provider.Provider_First_Name}`}
									name="Provider_NPI"
									value={provider.Provider_NPI}
								/>
							</div>
						))}
						{loading && (
							<div className="col-sm-12">
								<em className="fa fa-spin fa-circle-o-notch" />
							</div>
						)}
						{!loading && (providerOptions.length % PAGE_SIZE === 0) && (
							<div className="col-sm-4 col-sm-offset-8">
								<a
									href="#"
									className="btn btn-link"
									onClick={handleBumpOffset}
								>
									Load more...
								</a>
							</div>
						)}
					</div>
				</div>
				<div className="modal-footer">
					<Button
						title="Cancel"
						large
						onClick={onClose}
						disabled={isSaving || loading}
					/>
					<Button
						title="Save"
						onClick={handleSave}
						disabled={isSaving || loading}
						large
						primary
						className="ml"
					/>
				</div>
			</div>
		</Modal>
	);
};

ProviderFilterModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	isSaving: PropTypes.bool,
	currentFilter: PropTypes.object,
	providerId: PropTypes.number,
};

export default ProviderFilterModal;
